'use client';

import React from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function SourcingGuidePage() {
    const { t } = useTranslation();

    const steps = [
        {
            number: '01',
            title: t('defineRequirements'),
            description: t('defineRequirementsDesc'),
            icon: 'ClipboardDocumentListIcon',
        },
        {
            number: '02',
            title: t('searchFilter'),
            description: t('searchFilterDesc'),
            icon: 'MagnifyingGlassIcon',
        },
        {
            number: '03',
            title: t('verifyCompare'),
            description: t('verifyCompareDesc'),
            icon: 'ShieldCheckIcon',
        },
        {
            number: '04',
            title: t('requestQuotes'),
            description: t('requestQuotesDesc'),
            icon: 'ChatBubbleBottomCenterTextIcon',
        },
    ];

    const tips = [
        { icon: 'CheckBadgeIcon', title: t('verifyCredentials'), desc: t('verifyCredentialsDesc') },
        { icon: 'DocumentMagnifyingGlassIcon', title: t('reviewSamples'), desc: t('reviewSamplesDesc') },
        { icon: 'ChatBubbleLeftRightIcon', title: t('clearCommunication'), desc: t('clearCommunicationDesc') },
        { icon: 'ScaleIcon', title: t('compareOptions'), desc: t('compareOptionsDesc') },
        { icon: 'DocumentTextIcon', title: t('writtenAgreements'), desc: t('writtenAgreementsDesc') },
        { icon: 'ClockIcon', title: t('setTimelines'), desc: t('setTimelinesDesc') },
    ];

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero Section */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-semibold mb-6">
                            <Icon name="BookOpenIcon" size={16} variant="outline" />
                            <span>{t('comprehensiveGuide')}</span>
                        </div>
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">
                            {t('sourcingGuideTitle')}
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            {t('sourcingGuideSubtitle')}
                        </p>
                    </div>
                </section>

                {/* Steps */}
                <section className="py-20 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('theSourcingProcess')}</h2>
                        <div className="space-y-8">
                            {steps.map((step, index) => (
                                <div key={index} className="flex gap-6 items-start group">
                                    <div className="flex-shrink-0">
                                        <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                                            <Icon name={step.icon as any} size={28} variant="outline" className="text-primary group-hover:text-primary-foreground" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-5xl font-bold text-muted-foreground/10 mb-2">{step.number}</div>
                                        <h3 className="text-2xl font-headline font-bold text-foreground mb-3">{step.title}</h3>
                                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Best Practices */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('bestPractices')}</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {tips.map((tip, index) => (
                                <div key={index} className="bg-background border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                                    <Icon name={tip.icon as any} size={32} variant="solid" className="text-primary mb-4" />
                                    <h3 className="font-headline font-semibold text-foreground mb-2">{tip.title}</h3>
                                    <p className="text-sm text-muted-foreground">{tip.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 px-6">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">{t('readyToStartSourcing')}</h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            {t('browseMfgDirectory')}
                        </p>
                        <Link
                            href="/manufacturer-directory"
                            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-primary/90 transition-colors duration-200"
                        >
                            <span>{t('browseManufacturers')}</span>
                            <Icon name="ArrowRightIcon" size={20} variant="outline" />
                        </Link>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
